import React, { useEffect, useMemo, useState } from "react";
import FilesActionGroup from "../../../Reusable/FilesActionGroup/FilesActionGroup.js";
import useScrollTrigger from "../../../../hooks/useScrollTrigger.js";
import classNames from "classnames";
import SaveMailButton from "../../../Reusable/SaveMail/saveMailButton.js";
import Image from "../../../Elements/Image/Image.tsx";
import useMediaQueries from "../../../../hooks/useMediaQueries.js";
import AnimationContainer from "../../../Reusable/AnimationContainer/AnimationContainer.js";
import DownloadComponent from "./DownloadComponent.js";
import Adsense from "../../../Reusable/Adsense/Adsense.js";
import useMailSender from "../../../../hooks/useMailSender.js";
import IconStopPropagation from "../../../Reusable/Grid/Grid/IconStopPropagation.js";
import SearchAction from "components/Reusable/FilesActionGroup/SearchAction.js";
import useConditionToShowReupload from "components/Reusable/Reuploader/useConditionToShowReupload.tsx";
import useSelfRemovalLink from "hooks/useSelfRemovalLink.tsx";
import useShowPickerPromo from "components/Pages/Home/UploaderComponent/PickerPromo/hooks/useShowPickerPromo.ts";
import { useMail } from "../../../../contexts/mailContext/mailContext.js";
import { DaysLeft } from "../../../Reusable/DaysLeft/DaysLeft.js";
import { decode } from "html-entities";
import { ReuploadAction } from "components/Reusable/FilesActionGroup/ReuploadAction.js";
import { OwnerActions } from "./OwnerActions.tsx";
import { useUser } from "contexts/userContext/userContext.js";
import { homeColors } from "helpers/getBackgroundColor.js";
import useOrganization from "../../../../hooks/useOrganization.ts";

const FunctionalHeader = () => {
  const [currentPosition, setCurrentPosition] = useState(null);
  const { moreThanNumber: startedScrolling } = useScrollTrigger(20);
  const { mobileCondition, newTabletCondition } = useMediaQueries();

  const classes = classNames(["functional-header"]);

  useMemo(() => {
    if (document.querySelector(".uai")) {
      startedScrolling
        ? document.querySelector(".uai").classList.add("userway_bottom")
        : document.querySelector(".uai").classList.remove("userway_bottom");
    }
    startedScrolling
      ? setCurrentPosition(window.scrollY)
      : setCurrentPosition(null);
  }, [startedScrolling]);

  return (
    <div
      className={classes}
      onClick={() => {
        if (mobileCondition) window.scrollTo(0, 0);
      }}
    >
      <OwnerActions />
      <SenderAvatarAndInfo currentPosition={currentPosition} />
      <div className="flex-spacer"></div>
      {!(mobileCondition || newTabletCondition) ? (
        <>
          <MailActions
            currentPosition={currentPosition}
            startedScrolling={startedScrolling}
          />
          <div
            className={`ad-container ${
              startedScrolling ? "scrolled-down" : "flex-spacer"
            }`}
          >
            <Adsense bannerPosition="main-download-page" />
          </div>
          <DownloadComponent classNames="download-button" />
        </>
      ) : (
        <AnimationContainer
          animationOn={
            mobileCondition || startedScrolling || currentPosition > 100
          }
          animationClassName="fade-in"
          containerElementClassName="mail-actions-container"
        >
          <IconStopPropagation>
            <DownloadComponent classNames="download-button" />
          </IconStopPropagation>
        </AnimationContainer>
      )}
    </div>
  );
};

const MailActions = ({ currentPosition, startedScrolling }) => {
  const {
    getPaymentData,
    isMailAvailable,
    getIsDisplayAllowed,
    getMailSecurity,
    getMailMetaData,
    getIsCollection,
    getMailStatus,
    getMailId,
    getContributorsData,
  } = useMail();
  const { getUserData, isLoggedIn } = useUser();
  const { isMailSender } = useMailSender();
  // const {showPickerPromo} = useShowPickerPromo();
  const { checkCurrDomainIsOrgDomain } = useOrganization();

  const isCommentsHidden = checkCurrDomainIsOrgDomain() && !getUserData();

  const [viewActions, setViewActions] = useState([]);

  useEffect(
    () =>
      setViewActions(() =>
        getMailMetaData().HasFolders
          ? ["Sort", "FolderStructure", "View"]
          : ["Sort", "View"]
      ),
    [getMailMetaData().HasFolders]
  );

  const actionListEnum = {
    available: ["Collection", "Comment", "Share", "Info"],
    availableOwner: [
      "Collection",
      "Comment",
      "Security",
      "Share",
      "Info",
      // "Picker",
      ...(getMailMetaData().DoesContainVideo &&
      !getPaymentData() &&
      !getMailMetaData().IsVideoTranscoded
        ? ["VideoPreview"]
        : []),
    ],
    default: ["Comment", "Info"],
  };

  const isSelfRemove = useSelfRemovalLink();

  const isTopUserOfSender = getMailMetaData().AdminAccessEmails?.includes(
    getUserData().UserName
  );

  // adding contributors action if there are contributors
  useEffect(() => {
    const contributorsLength = Object.keys(getContributorsData())?.length;
    if (contributorsLength > 1) {
      if (viewActions.includes("Contributors")) return;
      setViewActions((prev) => ["Contributors", ...prev]);
    }
  }, [getContributorsData()]);

  const globalActions = useMemo(() => {
    if (isSelfRemove) return ["DeleteMail"];
    return (
      (
        isMailAvailable() && getIsDisplayAllowed()
          ? isMailSender() || isTopUserOfSender
            ? actionListEnum.availableOwner
            : actionListEnum.available
          : actionListEnum.default
      )
        // Hide comment action if domain is organization domain and user is not logged in
        .filter((action) => (isCommentsHidden ? action !== "Comment" : true))
    );
  }, [
    isSelfRemove,
    isTopUserOfSender,
    isMailSender(),
    getMailMetaData().IsVideoTranscoded,
    getMailMetaData().DoesContainVideo,
  ]);

  // remove share action if sharing is disabled or recipients only
  useEffect(() => {
    if (
      (getMailSecurity()?.isSharingDisabled ||
        getMailSecurity()?.recipientsOnly) &&
      !isMailSender()
    ) {
      const shareIndex = globalActions.indexOf("Share");
      if (shareIndex !== -1) {
        globalActions.splice(shareIndex, 1);
      }
    }
  }, [getMailSecurity(), isMailSender()]);

  // remove picker action if mail is not eligible for picker
  // useEffect(() => {
  //   if (getMailMetaData() && showPickerPromo !== null) {
  //     const conditionToRemovePicker =
  //       !showPickerPromo ||
  //       !getMailMetaData().IsContainOnlyImages ||
  //       getMailMetaData().filesCount < 100;

  //     if (conditionToRemovePicker) {
  //       const pickerIndex = globalActions.indexOf("Picker");
  //       if (pickerIndex !== -1) {
  //         globalActions.splice(pickerIndex, 1);
  //       }
  //     }
  //   }
  // }, [showPickerPromo, getMailMetaData()]);

  const conditionToShowReupload = useConditionToShowReupload();

  return (
    <AnimationContainer
      animationOn={startedScrolling || currentPosition > 100}
      animationClassName="fade-in"
      containerElementClassName="mail-actions-container"
    >
      <div className="flex-row">
        <SearchAction />
        {!getPaymentData().DenyPreview &&
        isMailAvailable() &&
        getIsDisplayAllowed() ? (
          <>
            <FilesActionGroup actionList={viewActions} />
            <span className="vertical-divider" />
          </>
        ) : null}
        <FilesActionGroup
          actionList={globalActions.filter((ga) =>
            checkCurrDomainIsOrgDomain() && !isLoggedIn()
              ? ga !== "Comment"
              : true
          )}
        />
        {isMailAvailable() && getIsDisplayAllowed() ? (
          <span className="vertical-divider" />
        ) : null}
      </div>
      {/* {getMailStatus() !== "removed" && !isSelfRemove && (
        <div className="flex-row">
          <DaysLeft />
        </div>
      )} */}
      {getMailStatus() !== "removed" ? (
        conditionToShowReupload ? (
          <ReuploadAction mailId={getMailId()} />
        ) : (
          <div className="flex-row">
            <DaysLeft />
          </div>
        )
      ) : null}
      {getIsCollection() || isSelfRemove ? null : <SaveMailButton />}
    </AnimationContainer>
  );
};

const ContributorAvatar = ({ email }) => {
  const { getContributorsData } = useMail();

  const contributorAvatart = getContributorsData()[email].image;

  const contributorName = getContributorsData()[email].fullName;

  const randColor = useMemo(() => {
    return homeColors[Math.floor(Math.random() * homeColors.length)];
  }, []);

  const styleObj = useMemo(() => {
    return !contributorAvatart
      ? {
          backgroundColor: randColor,
        }
      : {};
  }, [contributorAvatart, randColor]);

  return (
    <div className="mail-sender-avatar" style={styleObj}>
      {contributorAvatart ? (
        <Image src={contributorAvatart} />
      ) : (
        (contributorName || email).charAt(0)
      )}
    </div>
  );
};

const SenderAvatarAndInfo = () => {
  const { getMailMetaData, getContributorsData } = useMail();

  const { moreThanNumber } = useScrollTrigger(window.innerHeight / 3.5);

  const contributorsList = Object.keys(getContributorsData());

  return (
    <div className={"mail-info"}>
      {contributorsList.map((email) => (
        <ContributorAvatar email={email} key={email} />
      ))}
      <AnimationContainer
        animationOn={moreThanNumber}
        animationClassName="fade-in"
      >
        <div className="mail-info-text">
          <span className="mail-subject">
            {decode(getMailMetaData().subjectText)}
          </span>
          <span className="mail-sender">
            {contributorsList.map(
              (email, i) =>
                getContributorsData()[email].fullName +
                (i === contributorsList.length - 1 ? "" : ", ")
            )}
          </span>
        </div>
      </AnimationContainer>
    </div>
  );
};
export default FunctionalHeader;
